import { countries } from "@/generated/countries";
import { removeSegments } from "@frend-digital/ecom-layer/seo";
import {
  defineRouting,
  GeoLocale,
  parseGeo,
  replaceLocale,
} from "@frend-digital/next/i18n";
import { defineNavigation } from "@frend-digital/next/i18n/navigation";
import { addLeadingSlash, removeTrailingSlash } from "@frend-digital/utils";
import { Result } from "@frend-digital/utils/results";

export const LANGUAGES = ["en"] as const;

export const localesWithCountries = LANGUAGES.flatMap((locale) => {
  return countries.map((country) => `${locale}-${country.country}`);
});

export const routing = defineRouting({
  languages: LANGUAGES,
  defaultLanguage: "en",
  defaultLocale: "en-NO",
  locales: localesWithCountries,
  localePrefix: "always",
  countries,
  preferredCountries: ["NO"],
  groups: ["categories", "products", "pages"],
  domain: "https://blstworld.com",
  removeSegments: [
    "collections",
    "products",
    "pages",
    "blogs",
    "home",
    ...LANGUAGES,
  ],
});

export const {
  Link,
  Form,
  permanentRedirect,
  redirect,
  usePathname,
  useRouter,
  useGeo,
  SearchParamLink,
  addLocale,
} = defineNavigation(routing);

export const withDomain = (locale: string, uri: string) => {
  return `${routing.domain}${addLeadingSlash(
    removeTrailingSlash(
      replaceLocale(uri, locale || routing.defaultLocale, routing),
    ),
  )}`;
};

export const countryData = (country: string) => {
  const foundCountry = countries.find((c) => c.country === country);
  if (!foundCountry) return;

  return {
    ...foundCountry,
    pricelist: Number.parseInt(foundCountry.pricelist),
    market: Number.parseInt(foundCountry.market),
  };
};

export const getCountry = (geo: GeoLocale) => {
  const { country, language } = Result.unwrap(parseGeo(geo));

  const data = countryData(country);
  if (!data) {
    throw new Error("No country data found");
  }

  const { market, pricelist } = data;

  return {
    market,
    pricelist,
    language,
    country,
  };
};

export const getFinalUrl = (locale: string, path: string) => {
  return withDomain(locale, removeSegments(path, routing.removeSegments));
};

export const sitemapLocales = [
  ...countries.map((country) => `${country.language}-${country.country}`),
  ...localesWithCountries.filter((c) => c.includes("en-")),
];
